import React, { useContext, useMemo, useState } from 'react'
import moment from 'moment'
import {
  Box,
  Chip,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import CloseIcon from '@mui/icons-material/Close'
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded'
import HomeRepairServiceRoundedIcon from '@mui/icons-material/HomeRepairServiceRounded'
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded'
import BuildRoundedIcon from '@mui/icons-material/BuildRounded'
import PaidRoundedIcon from '@mui/icons-material/PaidRounded'

import StatCard from '../../../../../components/Cards/StatCard'
import AttachmentTiles from './AttachmentTiles'
import AddNewService, { serviceModalStyle } from './AddService'
import BigModal from '../../../../../components/Modals/BigModal'
import { TenantSettingsContext } from '../../../../../App'

const niceDate = (d) => (d ? moment(d).format('DD/MM/YYYY') : '—')

const ServiceModal = ({ selectedService, setSelectedService }) => {
  const dateLabel = useMemo(
    () => niceDate(selectedService?.date),
    [selectedService?.date],
  )
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig

  const [editService, setEditService] = useState(false)
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        overflow: 'hidden',
        bgcolor: 'background.default',
        border: (t) =>
          `1px solid ${t.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.10)'
            : 'rgba(0,0,0,0.10)'
          }`,
      }}
    >
      {/* Header */}
      <Box sx={{ px: 3, py: 2, bgcolor: 'background.paper' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          <Typography
            variant="title2"
            sx={{ fontWeight: 900, letterSpacing: -0.3 }}
            noWrap
          >
            Service
          </Typography>
          <Stack direction="row" gap={1} alignItems="center">
            <IconButton
              onClick={() => setEditService(true)}
              size="small"
              aria-label="Edit"
            >
              <EditIcon />
            </IconButton>
            <IconButton onClick={() => setSelectedService(null)} size="small" aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
      </Box>

      <Divider sx={{ opacity: 0.6 }} />

      {/* Body */}
      <Box sx={{ p: 3, maxHeight: '75vh', overflowY: 'auto' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={CalendarMonthRoundedIcon}
              label="Date"
              value={dateLabel}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={HomeRepairServiceRoundedIcon}
              label="Garage"
              value={selectedService?.garage ?? '—'}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={SpeedRoundedIcon}
              label="Mileage"
              value={
                selectedService?.mileage !== undefined &&
                  selectedService?.mileage !== null
                  ? `${selectedService.mileage}`
                  : '—'
              }
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={PaidRoundedIcon}
              label="Cost"
              value={
                selectedService?.cost != null
                  ? `${orgSettings?.currency ?? '£'}${Number(selectedService.cost).toFixed(2)}`
                  : '—'
              }
            />
          </Grid>

          {/* Work Completed */}
          <Grid item xs={12} md={7}>
            <Paper
              variant="outlined"
              sx={{
                p: 2.5,
                borderRadius: 3,
                bgcolor: 'background.paper',
                boxShadow: (t) =>
                  t.palette.mode === 'dark'
                    ? 'none'
                    : '0 2px 10px rgba(0,0,0,0.04)',
              }}
            >
              <Typography
                variant="overline"
                sx={{ opacity: 0.65, letterSpacing: 0.8 }}
              >
                Work Completed
              </Typography>

              <Typography
                variant="body1"
                sx={{ mt: 0.75, whiteSpace: 'pre-wrap' }}
              >
                {selectedService?.workCompleted?.trim()
                  ? selectedService.workCompleted
                  : 'No work details recorded.'}
              </Typography>
            </Paper>
          </Grid>

          {/* Attachments */}
          <Grid item xs={12} md={5}>
            <Stack gap={1}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  bgcolor: 'background.paper',
                  boxShadow: (t) =>
                    t.palette.mode === 'dark'
                      ? 'none'
                      : '0 2px 10px rgba(0,0,0,0.04)',
                }}
              >
                <Stack
                  direction="row"
                  alignItems="baseline"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="overline"
                    sx={{ opacity: 0.65, letterSpacing: 0.8 }}
                  >
                    Attachments
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.65 }}>
                    {selectedService?.documents?.length
                      ? `${selectedService.documents.length} file(s)`
                      : 'No files'}
                  </Typography>
                </Stack>

                <AttachmentTiles documents={selectedService?.documents} small />
              </Paper>
              {selectedService?.updatedAt && (
                <Typography
                  variant="body2"
                  sx={{ opacity: 0.7 }}
                  noWrap
                  textAlign="end"
                >
                  Updated by {selectedService?.updatedBy?.firstName}{' '}
                  {selectedService?.updatedBy?.lastName} at{' '}
                  {moment(selectedService.updatedAt).format(
                    'HH:mm [on] Do MMM YY',
                  )}
                </Typography>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Box>
      <BigModal
        open={editService}
        onClose={() => setEditService(false)}
        style={serviceModalStyle}
      >
        <AddNewService onClose={() => setEditService(false)} existingService={selectedService} />
      </BigModal>
    </Paper>
  )
}

export default ServiceModal
